import {
	getMappedCollectionEntity,
	getMappedMainEntity,
	getRuntimeBrandKey,
	getRuntimeCollectionEntity,
	getRuntimeMainEntity,
	type CollectionEntity,
	type MainEntity,
} from '#app/utils/pz/brand/routeEntityRuntime'

export type RuntimeBrandInterpolationConfig = {
	key?: string
	displayName?: string
	shortName?: string
	domain?: string
	contactEmail?: string
	description?: string
	legalName?: string
	legalAddress?: string
	legalCountry?: string
	legalRegistration?: string
	legalEntityType?: string
	legalPersonName?: string
	legalTaxId?: string
	mainEntity?: MainEntity
	collectionEntity?: CollectionEntity
	defaultLanguage?: string
}

export type RuntimeBrandEnv = {
	BRAND?: string
	BRAND_MAIN_ENTITY?: MainEntity
	BRAND_COLLECTION_ENTITY?: CollectionEntity
	BRAND_DEFAULT_LANGUAGE?: string
	BRAND_DISPLAY_NAME?: string
	BRAND_SHORT_NAME?: string
	BRAND_DOMAIN?: string
	BRAND_CONTACT_EMAIL?: string
	BRAND_DESCRIPTION?: string
	BRAND_LEGAL_NAME?: string
	BRAND_LEGAL_ADDRESS?: string
	BRAND_LEGAL_COUNTRY?: string
	BRAND_LEGAL_REGISTRATION?: string
	BRAND_LEGAL_ENTITY_TYPE?: string
	BRAND_LEGAL_PERSON_NAME?: string
	BRAND_LEGAL_TAX_ID?: string
}

function getRuntimeBrandEnv(): RuntimeBrandEnv | undefined {
	if (typeof window !== 'undefined') {
		return (window as typeof window & { ENV?: RuntimeBrandEnv }).ENV
	}

	return (globalThis as typeof globalThis & { ENV?: RuntimeBrandEnv }).ENV
}

export function getRuntimeBrandInterpolationConfig(
	brandKey?: string | null,
): RuntimeBrandInterpolationConfig {
	const env = getRuntimeBrandEnv()
	const resolvedBrandKey = brandKey ?? env?.BRAND ?? getRuntimeBrandKey()
	return buildRuntimeBrandInterpolationConfig(env, resolvedBrandKey)
}

export function buildRuntimeBrandInterpolationConfig(
	env?: RuntimeBrandEnv,
	brandKey?: string | null,
): RuntimeBrandInterpolationConfig {
	const resolvedBrandKey = brandKey ?? env?.BRAND

	return {
		key: resolvedBrandKey,
		displayName: env?.BRAND_DISPLAY_NAME,
		shortName: env?.BRAND_SHORT_NAME,
		domain: env?.BRAND_DOMAIN,
		contactEmail: env?.BRAND_CONTACT_EMAIL,
		description: env?.BRAND_DESCRIPTION,
		legalName: env?.BRAND_LEGAL_NAME,
		legalAddress: env?.BRAND_LEGAL_ADDRESS,
		legalCountry: env?.BRAND_LEGAL_COUNTRY,
		legalRegistration: env?.BRAND_LEGAL_REGISTRATION,
		legalEntityType: env?.BRAND_LEGAL_ENTITY_TYPE,
		legalPersonName: env?.BRAND_LEGAL_PERSON_NAME,
		legalTaxId: env?.BRAND_LEGAL_TAX_ID,
		mainEntity:
			env?.BRAND_MAIN_ENTITY ??
			getRuntimeMainEntity() ??
			getMappedMainEntity(resolvedBrandKey),
		collectionEntity:
			env?.BRAND_COLLECTION_ENTITY ??
			getRuntimeCollectionEntity() ??
			getMappedCollectionEntity(resolvedBrandKey),
		defaultLanguage: env?.BRAND_DEFAULT_LANGUAGE,
	}
}

export function buildBrandInterpolationVariables(
	brand: RuntimeBrandInterpolationConfig,
	language: string,
): Record<string, string> {
	const mainEntity = brand.mainEntity ?? 'course'
	const isSpanish = language === 'es'
	const mainEntityLabels = {
		course: {
			singular: { en: 'Course', es: 'Curso' },
			plural: { en: 'Courses', es: 'Cursos' },
			gender: { en: 'n', es: 'm' },
		},
		track: {
			singular: { en: 'Track', es: 'Ruta' },
			plural: { en: 'Tracks', es: 'Rutas' },
			gender: { en: 'n', es: 'f' },
		},
	}
	const entityLabels = mainEntityLabels[mainEntity] ?? mainEntityLabels.course
	const mainEntityLabel =
		entityLabels.singular[language as 'en' | 'es'] ?? entityLabels.singular.en
	const mainEntityLabelPlural =
		entityLabels.plural[language as 'en' | 'es'] ?? entityLabels.plural.en
	const mainEntityGender =
		entityLabels.gender[language as 'en' | 'es'] ?? entityLabels.gender.en
	const isFeminine = isSpanish && mainEntityGender === 'f'

	const collectionEntity = brand.collectionEntity ?? 'collection'
	const collectionEntityLabels = {
		collection: {
			singular: { en: 'Collection', es: 'Colección' },
			plural: { en: 'Collections', es: 'Colecciones' },
			gender: { en: 'n', es: 'f' },
		},
		roadmap: {
			singular: { en: 'Roadmap', es: 'Itinerario' },
			plural: { en: 'Roadmaps', es: 'Itinerarios' },
			gender: { en: 'n', es: 'm' },
		},
		program: {
			singular: { en: 'Program', es: 'Programa' },
			plural: { en: 'Programs', es: 'Programas' },
			gender: { en: 'n', es: 'm' },
		},
	}
	const colEntityLabels =
		collectionEntityLabels[collectionEntity] ??
		collectionEntityLabels.collection
	const collectionEntityLabel =
		colEntityLabels.singular[language as 'en' | 'es'] ??
		colEntityLabels.singular.en
	const collectionEntityLabelPlural =
		colEntityLabels.plural[language as 'en' | 'es'] ?? colEntityLabels.plural.en
	const collectionEntityGender =
		colEntityLabels.gender[language as 'en' | 'es'] ?? colEntityLabels.gender.en
	const isColFeminine = isSpanish && collectionEntityGender === 'f'

	const legalEntityType = brand.legalEntityType ?? 'empresa'
	const legalName = brand.legalName ?? brand.displayName ?? brand.key ?? ''
	const legalPersonName = brand.legalPersonName ?? legalName
	const legalTaxId = brand.legalTaxId ?? ''
	const legalRegistration = brand.legalRegistration ?? ''
	const legalCountryRaw = brand.legalCountry ?? ''
	const brandLegalCountry =
		isSpanish && legalCountryRaw.toLowerCase() === 'spain'
			? 'España'
			: legalCountryRaw
	const legalTaxIdLabel = legalEntityType === 'autonomo' ? 'NIF' : 'CIF'
	const legalEntityTypeLabel = isSpanish
		? legalEntityType === 'autonomo'
			? 'autónomo'
			: 'empresa'
		: legalEntityType === 'autonomo'
			? 'self-employed sole trader'
			: 'company'
	const brandLegalOperator =
		legalEntityType === 'autonomo'
			? `${legalPersonName}, ${legalEntityTypeLabel}${legalTaxId ? ` (${legalTaxIdLabel}: ${legalTaxId})` : ''}`
			: `${legalName}${legalTaxId ? ` (${legalTaxIdLabel}: ${legalTaxId})` : ''}`
	const brandLegalRepresentativeLine = isSpanish
		? legalEntityType === 'autonomo'
			? `Titular: ${legalPersonName}.`
			: `Representante o persona de contacto: ${legalPersonName}.`
		: legalEntityType === 'autonomo'
			? `Owner: ${legalPersonName}.`
			: `Representative or contact person: ${legalPersonName}.`
	const brandLegalRegistrationLine = legalRegistration
		? isSpanish
			? `Información registral: ${legalRegistration}.`
			: `Registration details: ${legalRegistration}.`
		: ''

	return {
		brandName: brand.displayName ?? legalName,
		brandShortName: brand.shortName ?? brand.displayName ?? legalName,
		brandDomain: brand.domain ?? '',
		brandContact: brand.contactEmail ?? '',
		brandDescription: brand.description ?? '',
		brandLegalName: legalName,
		brandLegalAddress: brand.legalAddress ?? '',
		brandLegalCountry,
		brandLegalRegistration: legalRegistration || legalTaxId,
		brandLegalEntityType: legalEntityType,
		brandLegalEntityTypeLabel: legalEntityTypeLabel,
		brandLegalPersonName: legalPersonName,
		brandLegalTaxId: legalTaxId,
		brandLegalTaxIdLabel: legalTaxIdLabel,
		brandLegalOperator,
		brandLegalRepresentativeLine,
		brandLegalRegistrationLine,
		mainEntityLabel,
		mainEntityLabelPlural,
		mainEntityLabelLower: mainEntityLabel.toLowerCase(),
		mainEntityLabelPluralLower: mainEntityLabelPlural.toLowerCase(),
		mainEntityGender,
		mainEntityGenderSuffix: isSpanish ? (isFeminine ? 'a' : 'o') : 'o',
		mainEntityGenderSuffixPlural: isSpanish ? (isFeminine ? 'as' : 'os') : 'os',
		mainEntityArticle: isSpanish ? (isFeminine ? 'la' : 'el') : 'the',
		mainEntityArticleLower: (isSpanish
			? isFeminine
				? 'la'
				: 'el'
			: 'the'
		).toLowerCase(),
		mainEntityArticlePlural: isSpanish ? (isFeminine ? 'las' : 'los') : 'the',
		mainEntityArticlePluralLower: (isSpanish
			? isFeminine
				? 'las'
				: 'los'
			: 'the'
		).toLowerCase(),
		mainEntityArticleWithDe: isSpanish
			? isFeminine
				? 'de la'
				: 'del'
			: 'of the',
		mainEntityArticleWithDeLower: (isSpanish
			? isFeminine
				? 'de la'
				: 'del'
			: 'of the'
		).toLowerCase(),
		mainEntityArticleWithA: isSpanish ? (isFeminine ? 'a la' : 'al') : 'to the',
		mainEntityArticleWithALower: (isSpanish
			? isFeminine
				? 'a la'
				: 'al'
			: 'to the'
		).toLowerCase(),
		mainEntityDemonstrative: isSpanish
			? isFeminine
				? 'esta'
				: 'este'
			: 'this',
		mainEntityDemonstrativeLower: (isSpanish
			? isFeminine
				? 'esta'
				: 'este'
			: 'this'
		).toLowerCase(),
		mainEntityDemonstrativePlural: isSpanish
			? isFeminine
				? 'estas'
				: 'estos'
			: 'these',
		mainEntityDemonstrativePluralLower: (isSpanish
			? isFeminine
				? 'estas'
				: 'estos'
			: 'these'
		).toLowerCase(),
		collectionEntityLabel,
		collectionEntityLabelPlural,
		collectionEntityLabelLower: collectionEntityLabel.toLowerCase(),
		collectionEntityLabelPluralLower: collectionEntityLabelPlural.toLowerCase(),
		collectionEntityGender,
		collectionEntityGenderSuffix: isSpanish ? (isColFeminine ? 'a' : 'o') : 'o',
		collectionEntityGenderSuffixPlural: isSpanish
			? isColFeminine
				? 'as'
				: 'os'
			: 'os',
		collectionEntityArticle: isSpanish ? (isColFeminine ? 'la' : 'el') : 'the',
		collectionEntityArticleLower: (isSpanish
			? isColFeminine
				? 'la'
				: 'el'
			: 'the'
		).toLowerCase(),
		collectionEntityArticlePlural: isSpanish
			? isColFeminine
				? 'las'
				: 'los'
			: 'the',
		collectionEntityArticlePluralLower: (isSpanish
			? isColFeminine
				? 'las'
				: 'los'
			: 'the'
		).toLowerCase(),
		collectionEntityArticleWithDe: isSpanish
			? isColFeminine
				? 'de la'
				: 'del'
			: 'of the',
		collectionEntityArticleWithDeLower: (isSpanish
			? isColFeminine
				? 'de la'
				: 'del'
			: 'of the'
		).toLowerCase(),
		collectionEntityArticleWithA: isSpanish
			? isColFeminine
				? 'a la'
				: 'al'
			: 'to the',
		collectionEntityArticleWithALower: (isSpanish
			? isColFeminine
				? 'a la'
				: 'al'
			: 'to the'
		).toLowerCase(),
		collectionEntityDemonstrative: isSpanish
			? isColFeminine
				? 'esta'
				: 'este'
			: 'this',
		collectionEntityDemonstrativeLower: (isSpanish
			? isColFeminine
				? 'esta'
				: 'este'
			: 'this'
		).toLowerCase(),
		collectionEntityDemonstrativePlural: isSpanish
			? isColFeminine
				? 'estas'
				: 'estos'
			: 'these',
		collectionEntityDemonstrativePluralLower: (isSpanish
			? isColFeminine
				? 'estas'
				: 'estos'
			: 'these'
		).toLowerCase(),
	}
}
